import styled from '@emotion/styled';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { CHAT_CARD_LAYOUT } from '../../aisdk';
import { useAiPanelLayout } from './AiPanelLayoutContext';
import {
  CONCIERGE_LAUNCHER_ELEMENT_ID,
  CONCIERGE_SIDEBAR_INSET_PX,
  CONCIERGE_SIDEBAR_WIDTH_PX,
} from './constants';
import { useAIChatState } from './useAIChatState';
import { useTopSpacing } from './useTopSpacing';

const MIN_PANEL_HEIGHT_PX = 240;

const PANEL_TRANSITION_MS = CHAT_CARD_LAYOUT.transitionDurationMs;
const PANEL_EASING = CHAT_CARD_LAYOUT.transitionEasing;

/** Sticky band: top inset below chrome, height fills viewport minus top/bottom insets. */
const Column = styled.div<{ $open: boolean }>`
  box-sizing: border-box;
  min-width: 0;
  min-height: 0;
  width: 100%;
  height: ${({ $open }) => ($open ? 'auto' : '100%')};
  padding: ${({ $open }) => ($open ? CONCIERGE_SIDEBAR_INSET_PX : 0)}px;
  overflow: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: ${({ $open }) =>
    $open
      ? `opacity ${PANEL_TRANSITION_MS}ms ${PANEL_EASING}, visibility 0ms`
      : `opacity ${PANEL_TRANSITION_MS}ms ${PANEL_EASING}, visibility 0ms linear ${PANEL_TRANSITION_MS}ms`};
  display: flex;
  flex-direction: column;
  align-self: ${({ $open }) => ($open ? 'start' : 'stretch')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Mount = styled.div`
  flex: 1;
  min-height: 0;
  width: ${CONCIERGE_SIDEBAR_WIDTH_PX}px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  max-height: 100%;
  overscroll-behavior-y: contain;
`;

function useAiShellViewportBand(
  isOpen: boolean,
  shellGridNode: HTMLDivElement | null | undefined,
) {
  const topSpacing = useTopSpacing();

  const [band, setBand] = useState<{ stickyTop: number; maxHeight: number }>({
    stickyTop: 0,
    maxHeight: MIN_PANEL_HEIGHT_PX,
  });

  const update = useCallback(() => {
    if (!shellGridNode || !isOpen) {
      return;
    }
    const stickyTop = topSpacing || 0;
    const maxHeight = Math.max(
      MIN_PANEL_HEIGHT_PX,
      Math.round(window.innerHeight - stickyTop),
    );
    setBand({ stickyTop, maxHeight });
  }, [shellGridNode, isOpen, topSpacing]);

  useLayoutEffect(() => {
    if (!shellGridNode || !isOpen) {
      return;
    }
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(document.documentElement);
    ro.observe(shellGridNode);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [update, shellGridNode, isOpen]);

  return band;
}

/**
 * When the pointer is over the Concierge mount (portal root), keep wheel deltas from scrolling
 * the page unless an inner scroll region (chat/history) can absorb them. Attached to the mount
 * only—not the padded grid column—so wheels on the column shell do not hit capture+preventDefault,
 * and events over the main column never traverse this node. Uses non-passive preventDefault;
 * pairs with overscroll-behavior on inner scroll surfaces.
 */
function useAiRailWheelIsolation(
  enabled: boolean,
  mountRef: React.RefObject<HTMLDivElement>,
) {
  useEffect(() => {
    const el = mountRef.current;
    if (!el || !enabled) {
      return;
    }

    const onWheel = (e: WheelEvent) => {
      if (!el.contains(e.target as Node)) {
        return;
      }
      for (const node of e.composedPath()) {
        if (node === el) {
          break;
        }
        if (!(node instanceof HTMLElement)) {
          continue;
        }
        const { overflowY } = window.getComputedStyle(node);
        if (
          (overflowY === 'auto' ||
            overflowY === 'scroll' ||
            overflowY === 'overlay') &&
          node.scrollHeight > node.clientHeight + 1
        ) {
          const { scrollTop, scrollHeight, clientHeight } = node;
          const maxScrollTop = scrollHeight - clientHeight;
          if (e.deltaY < 0 && scrollTop > 0) {
            return;
          }
          if (e.deltaY > 0 && scrollTop < maxScrollTop - 1) {
            return;
          }
          break;
        }
      }
      e.preventDefault();
    };

    el.addEventListener('wheel', onWheel, { capture: true, passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel, true);
    };
  }, [enabled, mountRef]);
}

/**
 * Grid right-rail slot for Zuora AI (`ShellContentGrid`). Sticky viewport band with symmetric
 * vertical insets so the panel never exceeds the viewport height. Mount node is used with
 * `createPortal` from Concierge.
 */
export const AiPanelSlot: React.FC = () => {
  const { isOpen } = useAIChatState();
  const layout = useAiPanelLayout();
  const panelMountRef = useRef<HTMLDivElement | null>(null);

  const band = useAiShellViewportBand(
    isOpen,
    layout?.shellGridNode ?? undefined,
  );

  useAiRailWheelIsolation(Boolean(isOpen && layout), panelMountRef);

  const setMountNode = layout?.setMountNode;
  const mountRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      panelMountRef.current = node;
      setMountNode?.(node);
    },
    [setMountNode],
  );

  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      document.getElementById(CONCIERGE_LAUNCHER_ELEMENT_ID)?.focus({
        preventScroll: true,
      });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  if (!layout) {
    return null;
  }

  const viewportStyle: React.CSSProperties | undefined = {
    position: 'sticky',
    top: band.stickyTop,
    height: band.maxHeight,
    maxHeight: band.maxHeight,
    zIndex: CHAT_CARD_LAYOUT.zIndex,
  };

  return (
    <Column
      $open={isOpen}
      style={viewportStyle}
      aria-hidden={!isOpen}
      data-e2e="unified-nav-ai-panel-column"
      inert={!isOpen ? true : undefined}
    >
      <Mount ref={mountRefCallback} />
    </Column>
  );
};
